import ReturnCodeInput from "@/components/common/ReturnCodeInput";
import RequestList from "@/components/request-select/RequestList";
import RequestSelect from "@/components/request-select/RequestSelect";
import SellerSelect from "@/components/supplier-select/SellerSelect";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RequestModes_ } from "@/constants/constants";
import { ReturnCreationTabTriggers } from "@/constants/tab-constants";
import { HUB_ } from "@/interfaces/HUB";
import { ReturnRequest } from "@/interfaces/Return";
import { Seller } from "@/interfaces/Seller";
import React, { useEffect, useState } from "react";
import { DialogFooter_ } from "./DialogFooter";
import { useDispatch } from "react-redux";
import { RequestProductDetailsDialog } from "../RequestProductDetailsDialog";
import { closeDialog } from "@/store/reducers/dialogReducer";
import { setRequestReturnHeader } from "@/store/reducers/requestReturnHeaderReducer";
import { useNavigate } from "react-router-dom";
import { NavigationRoutes } from "@/components/navigation/constants";
import RequestDropOffSelect from "@/components/dropoff-select/RequestDropOffSelect";

interface RequestCreationFormProps {}

const RequestCreationForm: React.FC<RequestCreationFormProps> = ({}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showSecondSection, setShowSecondSection] = useState(false);
  const [returnCode, setReturnCode] = useState<number>(0);
  const [mode, setMode] = useState<RequestModes_>(RequestModes_.NON_VENDABLE);
  const [selectedSeller, setSelectedSeller] = useState<Seller>({} as Seller);
  const [selectedRequests, setSelectedRequests] = useState<ReturnRequest[]>([]);
  const [selectedDropOff, setSelectedDropOff] = useState<HUB_>({} as HUB_);
  const [selectedRequest, setSelectedRequest] = useState<ReturnRequest>(
    {} as ReturnRequest
  );

  const handleShowDetails = (request: ReturnRequest) => {
    setSelectedRequest(request);
    if (request.num === selectedRequest.num) {
      setShowSecondSection(!showSecondSection);
    } else {
      setShowSecondSection(true);
    }
  };

  const handleRemoveRequest = (request: ReturnRequest) => {
    setSelectedRequests((prev) => prev.filter((r) => r.num !== request.num));
    setSelectedRequest({} as ReturnRequest);
    setShowSecondSection(false);
  };

  const disabledCheck = () => {
    return !(
      selectedSeller.sellerId &&
      returnCode !== 0 &&
      selectedRequests.length > 0
    );
  };

  const handleNavigate = () => {
    dispatch(
      setRequestReturnHeader({
        source: mode,
        seller: selectedSeller,
        returnCode: returnCode,
        request: selectedRequests[0],
        dropOff: selectedDropOff,
      })
    );
    navigate(NavigationRoutes.REQUEST_RETURN_CREATE);
    dispatch(closeDialog());
  };

  useEffect(() => {
    setSelectedSeller({} as Seller);
  }, [mode]);

  useEffect(() => {
    setShowSecondSection(false);
    if (selectedRequests.length === 0) {
      setSelectedRequest({} as ReturnRequest);
    }
  }, [selectedRequests]);

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          <ReturnCodeInput setReturnCode_={setReturnCode} />
          <Tabs defaultValue="stock-non-vendable" className="w-full">
            <TabsList className="flex flex-col sm:flex-row w-full h-full">
              {ReturnCreationTabTriggers.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  onClick={() => setMode(tab.mode)}
                  className="flex-1 whitespace-nowrap w-full"
                >
                  {tab.text}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <SellerSelect
            mode={mode}
            selectedSeller={selectedSeller}
            setSelectedSeller={setSelectedSeller}
          />
          {selectedSeller.sellerId && (
            <div className="flex flex-col gap-4">
              <RequestSelect
                mode={mode}
                sellerId={selectedSeller.sellerId}
                selectedRequests={selectedRequests}
                setSelectedRequests={setSelectedRequests}
              />
              {selectedRequests.length > 0 && (
                <RequestList
                  selectedRequests={selectedRequests}
                  handleRemoveRequest={handleRemoveRequest}
                  handleShowDetails={handleShowDetails}
                />
              )}
            </div>
          )}
          <div>
            <RequestDropOffSelect
              selectedRequest={selectedRequests[0]}
              selectedDropOff={selectedDropOff}
              setSelectedDropOff={setSelectedDropOff}
            />
          </div>
        </div>

        <DialogFooter_
          confirmationMode={false}
          disabled={disabledCheck()}
          loading={false}
          handleSubmit={handleNavigate}
        />
      </div>
      <RequestProductDetailsDialog
        isOpened={selectedRequest.num !== undefined}
        request={selectedRequest}
        handleClose={() => setSelectedRequest({} as ReturnRequest)}
      />
    </div>
  );
};

export default RequestCreationForm;
